<script>
import {useEvaluationFileStore} from "@/stores/Evaluation/EvaluationFile.js"
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import {watch} from "vue";
import {useSecurityStore} from "@/stores/Security.js";
import axios from "axios";

export default {
    props: {
        type: String,
        rootPath: String,
    },
    setup(props) {
        const {
            total,
            loading,
            filter,
        } = useSharedData();
        
        const {transformFilterValues} = useSharedMethod();
        
        const {$i18n, $router} = useTools();
        
        const evaluationFileStore = useEvaluationFileStore();
        const securityStore = useSecurityStore();
        
        filter.value.archive = false;
        
        const headers = [
            {
                text: "...",
                align: "left",
                sortable: false,
                value: "action",
                width: "0%"
            },
            {
                text: $i18n.t("common.code"),
                align: "left",
                sortable: true,
                value: "code",
            },
            {
                text: $i18n.t("common.label"),
                align: "left",
                sortable: true,
                value: "name",
            },
            /*{
                text: $i18n.t("data_select.SOCIO_PROFESSIONAL"),
                align: "left",
                sortable: false,
                value: "socioProfessionalNames",
            },*/
            {
                text: $i18n.t("module.occupation"),
                align: "left",
                sortable: false,
                value: "occupationNames",
            },
            {
                text: $i18n.t("common.comment"),
                align: "left",
                sortable: true,
                value: "description",
            },
        ];
        
        const actions = [
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                // access: () => securityStore.authorities['UPDATE_EVALUATION_PARAM'],
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${props.rootPath}.edit`, params: {id: item.id}})
                },
            },
            {
                title: $i18n.t('common.duplicate'),
                icon: 'mdi-plus-circle-multiple-outline',
                color: 'teal',
                // access: () => securityStore.authorities['UPDATE_EVALUATION_PARAM'],
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${props.rootPath}.duplicate`, params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                // access: () => securityStore.authorities['UPDATE_EVALUATION_PARAM'],
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${props.rootPath}.delete`, params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("common.archive"),
                icon: 'mdi-archive',
                color: 'purple',
                // access: (item) => !item.archive && securityStore.authorities['UPDATE_EVALUATION_PARAM'],
                access: (item) => !item.archive,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${props.rootPath}.archive`, params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("common.unarchive"),
                icon: 'mdi-archive',
                color: 'purple',
                // access: (item) => item.archive && securityStore.authorities['UPDATE_EVALUATION_PARAM'],
                access: (item) => item.archive,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${props.rootPath}.unarchive`, params: {id: item.id}})
                },
            },
        ];
        
        function findAll() {
            loading.value = true;
            filter.value.type = props.type;
            axios.get('/evaluationFiles', {params: transformFilterValues(filter.value)}).then((response) => {
                evaluationFileStore.setEvaluationFiles(response.data);
                total.value = response.data.length;
            }).finally(() => {
                loading.value = false;
            });
        }
        
        
        watch(() => filter.value, () => {
            findAll();
        }, {deep: true, immediate: true});

        
        function view(item) {
            $router.push({name: `${props.rootPath}.view`, params: {id: item.id}});
        }
        
        return {
            evaluationFileStore,
            securityStore,
            headers,
            actions,
            total,
            loading,
            filter,
            view,
            findAll,
        };
    },
};
</script>

<template>
    <div>
        <w-app
            :title="$t(`module.${rootPath}`)"
            :total="total"
        >
            <template v-slot:rightToolbar>
                <w-btn-reload
                    :loading="loading"
                    @press="findAll"
                />
                <w-btn-add
                    :show="true"
                    @press="$router.push({ name: `${rootPath}.add` })"
                />
                <w-btn
                    icon="mdi-plus-circle-multiple"
                    color="teal"
                    :text="$t('common.multiple_addition')"
                    @press="$router.push({ name: `${rootPath}.bulk_update` })"
                />
            </template>
            
            <template v-slot:content>
                <v-container fluid>
                    <v-row dense>
                        <v-col cols="3">
                            <w-text-field
                                v-model="filter.name"
                                :label="$t('common.name')"
                            />
                        </v-col>
                        <v-col cols="3">
                            <w-autocomplete
                                v-model="filter.archive"
                                :items="[
                                    {
                                        id: false,
                                        name: $t('common.in_progress')
                                    },
                                    {
                                        id: true,
                                        name: $t('common.archive')
                                    }
                                ]"
                                :label="$t('common.state')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
                <w-data-table
                    :headers="headers"
                    :actions="actions"
                    :items="evaluationFileStore.evaluationFiles"
                    :total="total"
                    :loading="loading"
                    sort-by="note"
                    @rowDbClick="view"
                >
                    <template v-slot:[`item.code`]="{ item }">
                        <w-chip :text="item.code"/>
                    </template>
                    <template v-slot:[`item.name`]="{ item }">
                        <w-inline-text bold :text="item.name"/>
                    </template>
                    <template v-slot:[`item.occupationNames`]="{ item }">
                        <w-chip-list :strings="item.occupationNames"/>
                    </template>
                    <template v-slot:[`item.socioProfessionalNames`]="{ item }">
                        <w-chip
                            v-for="(name, index) in item.socioProfessionalNames"
                            :key="`socio-${index}`"
                            outlined
                            color="brown"
                            :text="name"
                        />
                    </template>
                </w-data-table>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>
